// Spec 26 time-attendance — the per-day reading of one timesheet week (LT-01).
//
// A timesheet row is one number per bucket; the manager approving it and the
// employee signing it need the SEVEN DAYS behind those numbers: which were
// worked, which were leave, which were UBGT or rest days. This module builds
// that reading. It is a READ MODEL over three sources that each stay the single
// truth for their own fact:
//
//   worked hours  → time entries          (the caller buckets them by tenant day)
//   leave         → entity_pto_request     (approved / taken; never copied)
//   day kind      → entity_holiday + rest day of week
//
// Leave is deliberately NOT materialised as pto/unpaid time entries. Payroll
// already deducts unpaid leave from the leave ledger (worked_days); an entry
// carrying the same day would count it twice the moment anything exported it,
// and it would go stale the day the leave is cancelled. Reading the ledger live
// makes both impossible.
//
// Pure — no I/O. The caller supplies exactly the rows for the week.

import type { PartialDaySpec, TimesheetException } from "./entities.ts";
import { computeLeaveDuration, HOURS_PER_WORKING_DAY } from "./leave-duration.ts";
import type { WorkingWeek } from "./working-week.ts";

const MS_PER_DAY = 86_400_000;

export type TimesheetDayType = "workday" | "rest_day" | "public_holiday" | "public_holiday_half";

/** One approved/taken leave request, trimmed to what a week reading needs. */
export interface LeaveLedgerEntry {
  readonly requestId: string;
  /** `entity_leave_type.code`; null for a request whose policy carries no type row. */
  readonly leaveTypeCode: string | null;
  /**
   * True when the employer does not pay this leave's days: the payroll class
   * FROZEN at request time is `unpaid` or `sgk_benefit` (sick report,
   * maternity). Neither the leave type's live `paid` bit nor the balance-draw
   * flag: payroll deducts on the frozen class, so the screen must too or it
   * would show a paid day the payslip took money for. A report draws a balance
   * and is still not employer-paid.
   */
  readonly unpaid: boolean;
  /** YYYY-MM-DD, inclusive. */
  readonly startDate: string;
  readonly endDate: string;
  readonly partialDays?: readonly PartialDaySpec[];
}

export interface TimesheetDayLeave {
  readonly requestId: string;
  readonly leaveTypeCode: string | null;
  readonly unpaid: boolean;
  /** The hours THIS day draws (7,5 for a full day, the window for a partial one). */
  readonly hours: number;
}

export interface TimesheetDay {
  /** YYYY-MM-DD, tenant-local. */
  readonly date: string;
  readonly dayType: TimesheetDayType;
  readonly workedHours: number;
  readonly leave: readonly TimesheetDayLeave[];
  /** Paid + unpaid leave hours this day, capped at one working day. */
  readonly leaveHours: number;
  readonly unpaidLeaveHours: number;
  /**
   * True when payroll deducts this whole calendar day as unpaid leave. It is
   * wider than `unpaidLeaveHours > 0`: payroll counts every calendar day inside
   * an unpaid request without partial windows, weekends included, while leave
   * hours are drawn only on working days. A request WITH windows deducts whole
   * only its working days that carry no window, so its weekends stay false here.
   * The two are shown side by side rather than reconciled.
   */
  readonly payrollUnpaid: boolean;
  /** The employee is on leave this day: leave hours are drawn OR payroll deducts it. */
  readonly onLeave: boolean;
  /**
   * Leave leaves no room for work: payroll deducts the whole day, or the leave
   * hours already fill a working day. A partial window is NOT this — the rest
   * of that day is ordinary work, so an hour worked beside a two-hour leave
   * contradicts nothing.
   */
  readonly leaveFillsDay: boolean;
  /**
   * Worked hours that contradict approved leave (LT-03): the day is entirely
   * leave, or worked plus leave hours exceed one working day.
   */
  readonly leaveDayWorked: boolean;
}

export interface TimesheetDaysInput {
  /** First day of the reading, YYYY-MM-DD. A timesheet passes its Monday. */
  readonly weekStart: string;
  /** Days to build; a timesheet week is 7. */
  readonly dayCount?: number;
  /** Worked hours per tenant-local day. Days absent from the map are 0. */
  readonly workedHoursByDate: ReadonlyMap<string, number>;
  /** Full-day holidays (HolidayRepo.holidayDatesInRange) and half days. */
  readonly holidayDates: readonly string[];
  readonly halfDayHolidayDates: readonly string[];
  /** 0 = Sunday; the TR overtime policy's `rest_day_of_week`, default Sunday. */
  readonly restDayOfWeek: number;
  readonly leave: readonly LeaveLedgerEntry[];
  /** Calendar days payroll deducts (UnpaidLeaveRepo.unpaidDatesByEmployee). */
  readonly payrollUnpaidDates: readonly string[];
  /** The tenant's working week (LT-11), so leave hours use the definition that drew them; omitted = Mon-Fri. */
  readonly workingWeek?: WorkingWeek;
}

export interface TimesheetDaysResult {
  readonly days: readonly TimesheetDay[];
  /** Leave hours drawn in the reading, split by the frozen paid flag. */
  readonly ptoHours: number;
  readonly unpaidHours: number;
  /** One `leave_day_worked` per day whose worked hours contradict its leave. */
  readonly exceptions: readonly TimesheetException[];
}

export function addIsoDays(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

/** True when `day` is a Monday — the anchor every timesheet week starts on. */
export function isMondayIso(day: string): boolean {
  return new Date(`${day}T00:00:00.000Z`).getUTCDay() === 1;
}

/** Calendar date (YYYY-MM-DD) of `now` in `timeZone`; falls back to UTC on a zone Intl rejects. */
function localIsoDate(now: Date, timeZone: string): string {
  const format = (zone: string): string =>
    // en-CA only for its sortable YYYY-MM-DD shape; the value is never displayed.
    new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: zone }).format(now);
  try {
    return format(timeZone);
  } catch {
    return format("UTC");
  }
}

/**
 * The Monday that opens the current week in `timeZone`. The day-view route is
 * keyed by a Monday (400 otherwise) and the server bins punches by the local
 * calendar day, so the device's own "today" would ask for the wrong week for a
 * person across the date line from the zone; the zone is a parameter.
 */
export function currentWeekStart(now: Date, timeZone: string): string {
  const today = localIsoDate(now, timeZone);
  // getUTCDay: 0 = Sunday. Sunday belongs to the week that STARTED six days ago.
  const sinceMonday = (new Date(`${today}T00:00:00.000Z`).getUTCDay() + 6) % 7;
  return addIsoDays(today, -sinceMonday);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * The hours one request draws on one day, by the SAME rule that drew the
 * balance at request time (`computeLeaveDuration`): a working weekday of the
 * tenant's week, not a full-day holiday, 7,5 h or the partial window.
 * Re-deriving per day rather than spreading the stored total keeps a partial day
 * at its own length.
 */
function leaveHoursOnDay(
  entry: LeaveLedgerEntry,
  date: string,
  isHoliday: boolean,
  workingWeek: WorkingWeek | undefined,
): number {
  const partials = (entry.partialDays ?? []).filter((window) => window.date === date);
  return computeLeaveDuration({
    startDate: date,
    endDate: date,
    holidayDates: isHoliday ? [date] : [],
    ...(workingWeek !== undefined && { workingWeek }),
    ...(partials.length > 0 && { partialDays: partials }),
  }).hours;
}

export function buildTimesheetDays(input: TimesheetDaysInput): TimesheetDaysResult {
  const holidays = new Set(input.holidayDates);
  const halfDays = new Set(input.halfDayHolidayDates);
  const payrollUnpaid = new Set(input.payrollUnpaidDates);
  // Stable order so two requests overlapping on a day always split the cap the same way.
  const ledger = [...input.leave].sort((a, b) => (a.requestId < b.requestId ? -1 : a.requestId > b.requestId ? 1 : 0));

  const days: TimesheetDay[] = [];
  const exceptions: TimesheetException[] = [];
  let ptoHours = 0;
  let unpaidHours = 0;

  for (let i = 0; i < (input.dayCount ?? 7); i++) {
    const date = addIsoDays(input.weekStart, i);
    const isHoliday = holidays.has(date);
    const dayType: TimesheetDayType = isHoliday
      ? "public_holiday"
      : halfDays.has(date)
        ? "public_holiday_half"
        : new Date(`${date}T00:00:00.000Z`).getUTCDay() === input.restDayOfWeek
          ? "rest_day"
          : "workday";

    // Two overlapping requests would otherwise draw more than a working day.
    let remaining = HOURS_PER_WORKING_DAY;
    const leave: TimesheetDayLeave[] = [];
    for (const entry of ledger) {
      if (date < entry.startDate || date > entry.endDate || remaining <= 0) continue;
      const hours = Math.min(leaveHoursOnDay(entry, date, isHoliday, input.workingWeek), remaining);
      if (hours <= 0) continue;
      remaining = round2(remaining - hours);
      leave.push({ requestId: entry.requestId, leaveTypeCode: entry.leaveTypeCode, unpaid: entry.unpaid, hours });
    }

    const leaveHours = round2(leave.reduce((sum, item) => sum + item.hours, 0));
    const unpaidLeaveHours = round2(leave.filter((item) => item.unpaid).reduce((sum, item) => sum + item.hours, 0));
    ptoHours += leaveHours - unpaidLeaveHours;
    unpaidHours += unpaidLeaveHours;

    const workedHours = round2(input.workedHoursByDate.get(date) ?? 0);
    const deducted = payrollUnpaid.has(date);
    const onLeave = leaveHours > 0 || deducted;
    const leaveFillsDay = deducted || leaveHours >= HOURS_PER_WORKING_DAY;
    // A partial-day window shares its day with work: only hours that do not fit
    // beside it (leave + worked past one working day) contradict the ledger.
    // `onLeave` guards the whole rule: a day with no leave at all must never be
    // flagged just because it was worked past 7,5 h — that is overtime, not a
    // leave conflict.
    const leaveDayWorked =
      workedHours > 0 &&
      onLeave &&
      (leaveFillsDay || round2(workedHours + leaveHours) > HOURS_PER_WORKING_DAY);
    if (leaveDayWorked) {
      exceptions.push({
        kind: "leave_day_worked",
        date,
        description: `Worked ${workedHours}h on ${date}, which has ${leaveHours}h of approved leave`,
      });
    }
    days.push({
      date,
      dayType,
      workedHours,
      leave,
      leaveHours,
      unpaidLeaveHours,
      payrollUnpaid: deducted,
      onLeave,
      leaveFillsDay,
      leaveDayWorked,
    });
  }

  return { days, ptoHours: round2(ptoHours), unpaidHours: round2(unpaidHours), exceptions };
}
